import { HttpErrorResponse } from "@angular/common/http";
import { AfterViewInit, Component, Inject, inject, OnInit } from "@angular/core";
import { FormArray, FormControl, FormGroup, ValidatorFn } from "@angular/forms";
import {
	MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
	MatLegacyDialogRef as MatDialogRef,
} from "@angular/material/legacy-dialog";
import { WorkspaceUser } from "@app/_classes/timeghost";
import { ElRefDirective } from "@app/_directives/el-ref/el-ref.directive";
import { useAbsencesForUser } from "@app/_helpers/absence/utils";
import { ObserveFormGroupErrors, useFormErrorObservable } from "@app/_helpers/get-error-observable";
import { pushError } from "@app/_helpers/globalErrorHandler";
import { ErrorResponse, isWorkingHoursError, parseErrorResponse } from "@app/_helpers/is-error-object";
import { hasPermissionByKey, isAdmin, isSubordinate, isSupervisor } from "@app/_helpers/permission";
import { getComegoFutureLockedDate, getComegoLockedDate } from "@app/_helpers/timeLock";
import {
	coerceTimeFormat,
	createRxValue,
	distinctUntilChangedJson,
	fromRxValue,
	getActiveSchedule,
	nextTick,
	stringify,
} from "@app/_helpers/utils";
import { ClockService } from "@app/_services/clock.service";
import { AppService } from "@app/app.service";
import { ComegoSuggestionService } from "@app/services/come-and-go-suggestion.service";
import { RecordToolbarService } from "@app/shared/record-toolbar/record-toolbar.service";
import { Order } from "@datorama/akita";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { TranslateService } from "@ngx-translate/core";
import {
	ComegoQuery,
	ComegoService,
	ComegoTime,
	ComegoTimeType,
	Logger,
	UserSettings,
	UserSettingsQuery,
	Workspace,
} from "@timeghost/web-api";
import { addDays, addMinutes, areIntervalsOverlapping, endOfDay, format, isSameDay, isValid } from "date-fns/esm";
import { omit, pick } from "lodash-es";
import { combineLatest, map, of, startWith, switchMap, takeWhile, tap } from "rxjs";
import { firstBy } from "thenby";
import {
	ComeGoTimeControl,
	checkTimeOrder,
	generateTimeTableFromSchedule,
	maxLengthDescription,
} from "../come-and-go-create-dialog/come-and-go-create-dialog.component";
import { COMEGO_ICON_MAP, COMEGO_MAX_DESCRIPTION } from "../come-and-go-create-dialog/come-and-go-utils";

type TimesSingleControl = ComeGoTimeControl & { recording: FormControl<boolean>; id: FormControl<string> };
type TimesControl = FormGroup<TimesSingleControl>;
const log = new Logger("ComeAndGoSuggestionDialogComponent");

export const VerifyBlockComegoPast: (currentuser: () => UserSettings, choosenUser: () => string) => ValidatorFn = (
	_currentuser,
	_choosenUser
) => {
	return (form: FormControl<Date>) => {
		const blockComego = _currentuser().workspace.settings.blockComego;
		if (!blockComego) return null;
		const date = form.getRawValue();
		const currentuser = _currentuser();
		const choosenUser = _choosenUser();
		const minDate = getComegoLockedDate(currentuser, choosenUser);
		const maxDate = getComegoFutureLockedDate(currentuser, choosenUser);
		if (minDate && maxDate) {
			if (date < minDate || date > maxDate) return { blockEdit: true };
			else return null;
		} else if (minDate) {
			if (date < minDate) return { blockEdit: true };
		} else if (maxDate) {
			if (date > maxDate) return { blockEdit: true };
		} else return null;
	};
};
/** Close result: persist the original (violating) projection instead of the suggested fix. */
export const PROCEED_WITH_VIOLATION = Symbol("PROCEED_WITH_VIOLATION");

/** Whether a suggested row is a persisted entry kept as-is, or one the projection produced. */
export type SuggestionEntryOrigin = "existing" | "added";
export type SuggestionComegoTime = ComegoTime & { origin?: SuggestionEntryOrigin; typeId?: string };

export interface WorkingHoursUpdateData {
	time: { value: SuggestionComegoTime[]; date: Date };
	forceRecreateDay?: boolean;
	idsToDelete?: string[];
	pendingProjectionFix?: boolean;
	violationNotices?: Array<{
		message: string;
		args?: Record<string, unknown>;
		start?: string;
		end?: string;
	}>;
	projectTimeData?: {
		existingDuration: number;
		addDuration: number;
		totalDuration: number;
		totalBreakDuration: number;
	};
	/** Present only when the rest period between working days is the broken rule. */
	restData?: {
		side: "prev" | "next";
		/** Previous day's last end (prev) or next day's first start (next), as date + clock time. */
		adjacentClock: string;
		/** This day's first start (prev) or last end (next), as a clock time. */
		entryClock: string;
		/** Seconds. */
		availableRestDuration: number;
		/** Seconds. */
		requiredRestDuration: number;
	};
}

/** Returns true for client-side draft ids that were never persisted to the backend. */
const isDraftId = (id?: string | null): boolean => id?.startsWith("draft-") ?? false;

const resolveEntryTime = (value: string | Date | null | undefined, formDate: Date): Date | null => {
	if (value == null || value === "") return null;
	if (value instanceof Date) return value;
	if (typeof value === "string" && value.includes("T")) {
		const parsed = new Date(value);
		return Number.isNaN(parsed.getTime()) ? null : parsed;
	}
	return coerceTimeFormat(value, formDate);
};
export type ComeGoTime = Omit<ComegoTime, "workspace" | "id"> & { recording?: boolean; typeId?: string };

export type absenceSubcategories = {
	id: string;
	enabled: boolean;
	title: string;
	description?: string;
	durationType?: "basedOnTargetHours" | "customValue";
	duration?: string | null;
	isStatic?: boolean;
	visibleUsers: any[];
	visibleForGroups: any[];
};

@UntilDestroy()
@ObserveFormGroupErrors()
@Component({
	selector: "tg-come-and-go-suggestion-dialog",
	templateUrl: "./come-and-go-suggestion-dialog.component.html",
	styleUrls: ["./come-and-go-suggestion-dialog.component.scss"],
})
export class ComeAndGoSuggestionDialogComponent implements OnInit, AfterViewInit {
	readonly iconMap = COMEGO_ICON_MAP;
	readonly isAMPM$ = this.userSettingsQuery.select().pipe(map((x) => !x.settings?.timeFormat24h));
	readonly now$ = this.clock.now$;
	readonly error$ = createRxValue<ErrorResponse>();
	absenceSubcategories: {
		id: string;
		enabled: boolean;
		title: string;
		description?: string;
		durationType?: "basedOnTargetHours" | "customValue";
		duration?: string | null;
		isStatic?: boolean;
		visibleUsers: any[];
		visibleForGroups: any[];
	}[] = [];
	constructor(
		private ref: MatDialogRef<ComeAndGoSuggestionDialogComponent>,
		private userSettingsQuery: UserSettingsQuery,
		private app: AppService,
		private comegoService: ComegoService,
		private comegoQuery: ComegoQuery,
		private recordService: RecordToolbarService,
		private comegoSuggestion: ComegoSuggestionService,
		@Inject(MAT_DIALOG_DATA)
		private data: WorkingHoursUpdateData,
		private translate: TranslateService,
		private clock: ClockService
	) {}

	readonly isLoading = createRxValue(false);
	private activeSchedule: ReturnType<typeof getActiveSchedule> = null;
	readonly projectTimeData = this.data.projectTimeData;
	readonly restData = this.data.restData;
	readonly restBetweenDaysError = !!this.data.restData;
	/** Work ranges of the day's active schedule, e.g. "08:00 - 12:00 and 13:00 - 17:00". */
	scheduleRangesText: string | null = null;

	private get dailyMaxSeconds(): number {
		const wh = this.userSettingsQuery.getValue().workspace?.schedules?.workspace?.workinghours;
		return (wh?.dailyMaxHours ?? 0) * 3600;
	}
	get isOverDailyMaxWorkTime(): boolean {
		const max = this.dailyMaxSeconds;
		return max > 0 && (this.projectTimeData?.totalDuration ?? 0) > max;
	}
	/** Part of the new project time that still fits into the daily maximum, in seconds. */
	get creditableNewProjectTimeSeconds(): number {
		return Math.max(0, this.dailyMaxSeconds - (this.projectTimeData?.existingDuration ?? 0));
	}
	readonly group = new FormGroup({
		user: new FormControl<Workspace["users"][0]>(null),
		times: new FormArray<TimesControl>([], {
			validators: [
				checkTimeOrder(
					() => this.userSettingsQuery.getValue(),
					() => {
						const user = this.group?.getRawValue()?.user as WorkspaceUser;
						if (!user) return null;
						const id = user.id;
						return id;
					},
					() => {
						const date = this.group?.getRawValue()?.date as Date;
						return date;
					},
					() => {
						const date = this.group?.getRawValue()?.date as Date;
						if (!date || !isValid(date)) return null;
						return {
							absence: 0,
							pause: 0,
							work: 0,
						} as { [K in ComegoTimeType]: number };
					},
					// last working day check
					() => {
						const { date } = this.group?.getRawValue() || {};
						if (!date || !isValid(date)) return null;
						const currentUser = this.group?.getRawValue()?.user as WorkspaceUser;
						const user = {
							...this.userSettingsQuery.getValue(),
							...((currentUser?.id && { id: currentUser?.id }) || {}),
						};
						const pastDay = addDays(date.getTime(), -1);
						const lastWorkingDayTime = this.comegoQuery.getAll({
							filterBy: (x) => isSameDay(new Date(x.start), pastDay) && x.user?.id === user.id,
							sortBy(a, b) {
								const aend = new Date(a.end || new Date()),
									bend = new Date(b.end || new Date());
								return bend.getTime() - aend.getTime();
							},
						})?.[0];
						if (!lastWorkingDayTime) return null;
						return {
							...lastWorkingDayTime,
							start: new Date(lastWorkingDayTime.start),
							end: new Date(lastWorkingDayTime.end || new Date()),
						};
					},
					// next working day check
					() => {
						const { date } = this.group?.getRawValue() || {};
						if (!date || !isValid(date)) return null;
						const user = this.userSettingsQuery.getValue();
						const dayOfTime = addDays(date.getTime(), 1);
						const nextWorkingDayTime = this.comegoQuery.getAll({
							filterBy: (x) => isSameDay(new Date(x.start), dayOfTime) && x.user?.id === user.id,
							sortBy(a, b) {
								const atime = new Date(a.start || new Date()),
									btime = new Date(b.start || new Date());
								return atime.getTime() - btime.getTime();
							},
							sortByOrder: Order.ASC,
						})?.[0];
						if (!nextWorkingDayTime) return null;
						return {
							...nextWorkingDayTime,
							start: new Date(nextWorkingDayTime.start),
							end: new Date(nextWorkingDayTime.end || new Date()),
						};
					},

					{ ignoreRulesForRecordingTimes: false }
				),
				maxLengthDescription(),
			],
			updateOn: "blur",
		}),
		date: new FormControl<Date>(new Date(), {
			validators: [
				VerifyBlockComegoPast(
					() => this.userSettingsQuery.getValue(),
					() => {
						const user = this.group?.getRawValue()?.user as WorkspaceUser;
						if (!user) return null;
						const id = user.id;
						return id;
					}
				),
			],
		}),
	});

	readonly absenceSubcategories$value = createRxValue(
		this.userSettingsQuery.getValue().workspace?.["workinghours"]?.[
			"absenceSubcategories"
		] as absenceSubcategories[]
	);
	readonly isSubAbsence = combineLatest([
		this.absenceSubcategories$value.asObservable(),
		this.group.valueChanges.pipe(startWith(this.group.value)),
	]).pipe(
		map(([absences, formGroup]) => {
			formGroup = this.group.value;
			let isAbsences: boolean[] = [];
			formGroup.times.forEach((comegoGroup) => {
				isAbsences.push(!!absences.find((absence) => absence.id == comegoGroup.type));
			});
			return isAbsences;
		})
	);

	readonly workingHoursRules$ = this.userSettingsQuery.select((x) => x.workspace?.schedules?.workspace?.workinghours);

	readonly existingEntries$ = createRxValue<ComegoTime[]>([]);
	readonly duration$ = combineLatest([
		this.group.valueChanges.pipe(startWith(this.group.getRawValue())),
		this.existingEntries$.value$,
		this.now$,
	]).pipe(
		map(([_, existingEntries, now]) => {
			const x = this.group.getRawValue();
			const user = this.userSettingsQuery.getValue();
			const wh = user.workspace.schedules.workspace["workinghours"] as any;
			const shortBreakDuration = (wh?.minimumBreakDurationForCredit ?? 0.25) * 3600;

			const retainedExisting = this.getRetainedExistingEntries(existingEntries);
			const baseSum = retainedExisting.reduce(
				(acc, r) => {
					if (!r.start || !r.end) return acc;
					const start = new Date(r.start).getTime(),
						end = new Date(r.end).getTime();
					if (end < start) return acc;
					const sum = (end - start) / 1000;
					if (r.type === "work") acc.working += sum;
					else if (r.type === "pause") {
						acc.pause += sum;
						if (sum > shortBreakDuration) acc.realBreak += sum;
					} else if (r.type === "absence") acc.absence += sum;
					return acc;
				},
				{ working: 0, pause: 0, realBreak: 0, absence: 0 }
			);

			return x.times.reduce((acc, r) => {
				if (!r.start || !r.end) return acc;
				const start = coerceTimeFormat(r.start!).getTime(),
					end = r.recording ? now.getTime() : coerceTimeFormat(r.end!).getTime();
				if (end < start) return acc;
				const sum = (end - start) / 1000;
				if (r.type === "work") acc.working += sum;
				else if (r.type === "pause") {
					acc.pause += sum;
					if (sum > shortBreakDuration) acc.realBreak += sum;
				} else if (r.type === "absence") acc.absence += sum;
				else acc.absence += sum;
				return acc;
			}, baseSum);
		})
	);
	readonly overlappingExistingEntryIds$ = combineLatest([
		this.group.valueChanges.pipe(startWith(this.group.getRawValue())),
		this.existingEntries$.value$,
	]).pipe(
		map(([_, existingEntries]) => {
			const x = this.group.getRawValue();
			const times = x.times;
			const date = x.date;
			const overlappingIds = new Set<string>();

			const retainedExisting = this.getRetainedExistingEntries(existingEntries);
			if (!retainedExisting.length || !date) return [];

			for (const formEntry of times) {
				if (formEntry.type !== "work" && formEntry.type !== "pause") continue;
				if (!formEntry.start || !formEntry.end || formEntry.recording) continue;

				const formStart = coerceTimeFormat(formEntry.start, date);
				const formEnd = coerceTimeFormat(formEntry.end, date);
				if (!isValid(formStart) || !isValid(formEnd) || formStart >= formEnd) continue;

				for (const existing of retainedExisting) {
					if (existing.type !== "work" && existing.type !== "pause") continue;
					if (!existing.start || !existing.end || !existing.id) continue;

					const existingStart = new Date(existing.start);
					const existingEnd = new Date(existing.end);
					if (!isValid(existingStart) || !isValid(existingEnd) || existingStart >= existingEnd) continue;

					if (
						areIntervalsOverlapping(
							{ start: formStart, end: formEnd },
							{ start: existingStart, end: existingEnd }
						) &&
						!(
							formEnd.getTime() === existingStart.getTime() ||
							existingEnd.getTime() === formStart.getTime()
						)
					) {
						overlappingIds.add(existing.id);
					}
				}
			}

			return Array.from(overlappingIds);
		})
	);
	readonly workspace$canManageOthers = fromRxValue(
		this.userSettingsQuery.select((x) => isAdmin(x) || isSupervisor(x))
	);
	readonly canUpdateTimes$ = fromRxValue(
		this.userSettingsQuery.select().pipe(map((x) => hasPermissionByKey(x, "groupsCanComegoUpdateTimes")))
	);
	readonly preventDeviatingWorkingHours = this.userSettingsQuery
		.select()
		.pipe(
			map(
				(userSettings) =>
					userSettings.workspace?.schedules?.workspace?.workinghours?.preventDeviatingWorkingHours
			)
		);
	private readonly rulesEnforced$ = this.preventDeviatingWorkingHours.pipe(
		startWith(
			!!this.userSettingsQuery.getValue().workspace?.schedules?.workspace?.workinghours
				?.preventDeviatingWorkingHours
		)
	);
	/** True when existing + form entries combined would meet or exceed the daily max work duration (disables add button). */
	readonly isDailyMaxReachedOrExceededByCombined$ = this.rulesEnforced$.pipe(
		switchMap((enforced) => {
			if (!enforced) return of(false);
			return combineLatest([
				this.group.valueChanges.pipe(startWith(this.group.getRawValue())),
				this.existingEntries$.value$,
				this.userSettingsQuery.select(
					(u) =>
						(u.workspace?.schedules as { workspace?: { workinghours?: { dailyMaxHours?: number } } })
							?.workspace?.workinghours
				),
			]).pipe(
				map(([_, existingEntries, wh]) => {
					const raw = this.group.getRawValue();
					const date = raw?.date as Date | null;
					if (!date || !isValid(date)) return false;
					const formEntries = (raw?.times ?? []).map(
						(t: { start?: string; end?: string; type: ComegoTimeType }) => ({
							start: t.start ?? "",
							end: t.end ?? "",
							type: t.type,
						})
					);
					return this.comegoSuggestion.isDailyMaxReachedOrExceededByCombined(
						date,
						this.getRetainedExistingEntries(existingEntries),
						formEntries,
						wh ?? undefined
					);
				})
			);
		})
	);
	getErrorObservable(controlName: string) {
		const usersettings = this.userSettingsQuery.getValue();
		const aliasMap = {
			name: "time.name",
			start: "timer.time.start",
			end: "timer.time.end",
		};
		const aliasName = aliasMap[controlName] || controlName;
		return useFormErrorObservable(this, { respectState: true })(
			controlName,
			() => this.group.controls[controlName],
			{
				required: (error, ctrl) => {
					if (controlName === "task") return { content: "errors.record.desc-required", args: {} };
					if (controlName === "project") return { content: "errors.record.project-req", args: {} };
					if (controlName === "times") return { content: "errors.times.comego.required", args: {} };
					return {
						content: "errors.required",
						args: { field: aliasName },
					};
				},
				same: (error, ctrl) => {
					const aliasFields = Object.entries(ctrl.errors?.fields || {}).reduce(
						(acc, [key, value]) => ({ ...acc, [key]: aliasMap[value as string] }),
						{}
					);
					return { content: "errors.sameValue", args: { ...aliasFields } };
				},
				breakRule: (error) => {
					if (error.type === "break")
						return {
							content: "errors.times.comego.breakRuleNotice",
							args: pick(error, "workingHours", "minutesDue"),
						};
					if (error.type === "rest")
						return {
							content: "errors.times.comego.breakBetweenWorkingDays",
							args: { breakBetweenWorkingDays: error.value },
						};
					if (error.type === "maxHours")
						return { content: "errors.times.comego.dailyMaxHours", args: { dailyMaxHours: error.value } };
					return null;
				},
				minlength: (error, ctrl) => ({
					content: "errors.minlength",
					args: { field: aliasName, length: error.requiredLength },
				}),
				maxlength: (error, ctrl) => ({
					content: "errors.maxlength",
					args: { field: aliasName, length: error.requiredLength },
				}),
				maxlengthDescription: (error, ctrl) => ({
					content: "errors.maxlength",
					args: { field: "Description", length: COMEGO_MAX_DESCRIPTION },
				}),
				sameType: (error, ctrl) => {
					return { content: "Subsequent entries must not be the same type", args: {} };
				},
				missingType: (error, ctrl) => {
					return { content: "Entries are missing a stop entry", args: {} };
				},
				invalidType: (error, ctrl) => {
					return { content: 'Before last type must not be of type "pause"', args: {} };
				},
				intervening: (error, ctrl) => {
					return { content: "errors.times.comego.intervening", args: {} };
				},
				breakToShort: (error, ctrl) => {
					const settings = usersettings.workspace.schedules.workspace["workinghours"] as any;
					return {
						content: "errors.times.comego.shortBreak",
						args: { length: (settings?.minimumBreakDurationForCredit ?? 0.25) * 60 },
						warning: true,
					};
				},
				blockEdit: (error, ctrl) => {
					return { content: "errors.times.comego.blockEdit", args: {} };
				},
				blockEditFuture: (error, ctrl) => {
					return { content: "errors.times.comego.blockEditFuture", args: {} };
				},
				blockComegoPast: () => {
					const blockComego = usersettings.workspace.settings.blockComego;
					return {
						content: "errors.blockComegoPast",
						args: { value: blockComego.period, range: blockComego.range },
					};
				},
				blockComegoFuture: () => {
					const blockComego = usersettings.workspace.settings.blockComego;
					return {
						content: "errors.blockComegoFuture",
						args: { value: blockComego.futurePeriod, range: blockComego.futureRange },
					};
				},
			},
			(key) => key,
			{
				initialValidate: false,
			}
		);
	}

	readonly canAddTime = createRxValue(true);
	readonly enabledTypes = createRxValue<{ [K in ComegoTimeType]?: boolean }>({
		absence: true,
		pause: true,
		work: true,
	});
	private newWorkEntryDuration: number | null = null;
	private preExistingEntriesById = new Map<string, ComegoTime>();
	/** Keyed by control instance so the origin survives re-sorting of the rows. */
	private readonly originByControl = new WeakMap<TimesControl, SuggestionEntryOrigin>();
	private initialBoundsById = new Map<string, { start: string; end: string; type: ComegoTimeType }>();
	readonly showContactAdmin = createRxValue(false);
	async ngOnInit() {
		this.ref._containerInstance._config.autoFocus = false;
		this.ref.updateSize("640px");
		this.ref.addPanelClass(["mat-dialog-vanilla", "mat-dialog-relative"]);
		const now = this.clock.now();
		const userSettings = this.userSettingsQuery.getValue();
		const userFromTime = this.data.time?.value?.find((d) => d.user)?.user ?? { id: userSettings.id };
		const user = userSettings.workspace.users?.find((d) => d.id === userFromTime.id) as WorkspaceUser;
		this.userSettingsQuery
			.select()
			.pipe(untilDestroyed(this))
			.subscribe((user) => {
				this.canAddTime.next(hasPermissionByKey(user, "groupsCanComegoCreateTimes"));
				nextTick(() => this.applyLimitedUpdatePermissions());
			});
		this.group.patchValue({
			user,
			date: this.data.time?.date ?? now,
		});
		this.group.controls.user.disable({ emitEvent: false });
		this.group.controls.date.disable({ emitEvent: false });
		this.activeSchedule = getActiveSchedule({ ...userSettings, id: user.id }, this.group.getRawValue().date);
		this.scheduleRangesText = this.buildScheduleRangesText(user.id, this.group.getRawValue().date);
		this.loadExistingEntriesForRecreateFlow();

		this.group.updateValueAndValidity();

		this.newWorkEntryDuration = 30 * 60 * 1000; // default 30 min until API returns
		const userId = this.group.getRawValue()?.user?.id ?? this.userSettingsQuery.getValue().id;
		this.comegoSuggestion.getLastWorkEntryDurationMs(userId).then((duration) => {
			if (duration != null && duration > 0) this.newWorkEntryDuration = duration;
		});

		nextTick(() => {
			this.data.time.value?.forEach((t) => {
				const start = new Date(t.start);
				if (t.typeId) {
					t.type = t.typeId as ComegoTimeType;
				}
				const isRecording = !t.end;
				const end = t.end ? new Date(t.end) : isSameDay(start, now) ? addMinutes(now, 1) : endOfDay(start);
				this.addTimeRange(t.type, start, end, false, isRecording, t.name, t.id, t.origin);
			});
			this.group.controls.times.updateValueAndValidity();
			this.sortTimes(true);
			this.buildEntryProvenanceMaps();
			this.applyLimitedUpdatePermissions();
		});

		this.group.valueChanges
			.pipe(
				untilDestroyed(this),
				map((x) => x?.date),
				distinctUntilChangedJson()
			)
			.subscribe(async () => {
				const value = this.group.getRawValue();
				const u = value?.user;
				const date = value?.date;
				if (u && date)
					this.activeSchedule = getActiveSchedule({ ...this.userSettingsQuery.getValue(), id: u.id }, date);
				this.group.controls.times.updateValueAndValidity();
			});

		this.group.controls.times.valueChanges.pipe(untilDestroyed(this)).subscribe(() => {
			this.group.controls.times.controls.forEach((timeControl) => {
				const endControl = timeControl.get("end");
				if (endControl && endControl.value === "00:00") {
					endControl.patchValue("23:59", { emitEvent: false });
				}
			});
		});

		this.absenceSubcategories = userSettings?.workspace?.["workinghours"]?.["absenceSubcategories"] || [];
	}
	ngAfterViewInit(): void {
		this.group.updateValueAndValidity();
	}
	blurFormSort(ev: Event, currentIndex?: number, field?: "start" | "end") {
		const canSyncAdjacent = !this.group.pristine && this.group.controls.times.length >= 2;

		this.group.markAllAsTouched();
		const currentTarget = ev.currentTarget as HTMLElement;
		setTimeout(() => {
			if (currentTarget.contains(document.activeElement)) return;

			if (field === "end" && currentIndex !== undefined && currentIndex >= 0) {
				const currentControl = this.group.controls.times.controls[currentIndex];
				if (currentControl) {
					this.clampEntryToInitialBounds(currentControl, "end");
				}
			}

			if (field === "end" && currentIndex !== undefined && currentIndex >= 0 && canSyncAdjacent) {
				const timesControls = this.group.controls.times.controls;
				const nextIndex = currentIndex + 1;

				if (nextIndex < timesControls.length) {
					const currentControl = timesControls[currentIndex];
					const nextControl = timesControls[nextIndex];
					if (currentControl && nextControl) {
						const currentEndTime = currentControl.controls.end.value;
						if (currentEndTime) {
							nextControl.controls.start.patchValue(currentEndTime, { emitEvent: false });
							nextControl.updateValueAndValidity();
							currentControl.updateValueAndValidity();
						}
					}
				}
			}
		}, 0);
	}
	private sortTimes(force: boolean = false) {
		if (!force && (this.group.pristine || this.group.controls.times.length < 2)) return;
		const { times } = this.group.getRawValue();
		const sortBy = firstBy((d: any) => coerceTimeFormat(d.start).getTime(), "asc").thenBy(
			(d: any) => coerceTimeFormat(d.end ?? d.start).getTime(),
			"asc"
		);
		this.group.controls.times.controls = this.group.controls.times.controls.toSorted((a, b) =>
			sortBy(a.getRawValue(), b.getRawValue())
		);
		log.debug({ sortedTimes: times });
		this.group.updateValueAndValidity();
		this.group.controls.times.updateValueAndValidity();
		if (this.isFullyRestrictedOnOwnTimes() || this.isCreateOnlyOnOwnTimes()) {
			this.applyLimitedUpdatePermissions();
		}
	}
	calculateDuration(ctrl: TimesControl, ctrlIndex: number) {
		const value = ctrl.getRawValue();
		if (!value) return null;
		const start = coerceTimeFormat(value.start!).getTime(),
			end = coerceTimeFormat(value.end!).getTime();
		if (end < start) return null;
		return (end - start) / 1000;
	}
	get groupValue() {
		return this.group.getRawValue();
	}
	getEntryOrigin(ctrl: TimesControl): SuggestionEntryOrigin {
		return this.originByControl.get(ctrl) ?? "added";
	}
	addTimeRange(
		type: ComegoTimeType,
		start: Date,
		end: Date,
		emitChangesOnAdd: boolean = true,
		recording: boolean = false,
		name: string,
		timeId?: string,
		origin: SuggestionEntryOrigin = "added"
	) {
		const isExistingEntry = !!timeId;
		const newTimeGroup = new FormGroup<TimesSingleControl>({
			name: new FormControl<string>(name),
			id: new FormControl(timeId ?? null),
			start: new FormControl(format(start, "HH:mm")),
			end: new FormControl(format(end, "HH:mm")),
			type: new FormControl({ value: type, disabled: recording }),
			recording: new FormControl(recording),
			user: new FormControl(null),
		});

		this.group.controls.times.push(newTimeGroup, { emitEvent: false });
		this.originByControl.set(newTimeGroup, origin);

		this.captureInitialBounds(newTimeGroup);

		if (!isExistingEntry) {
			this.parseTimeDurationFromType(newTimeGroup);
		}

		newTimeGroup.controls.start.valueChanges.subscribe(() => {
			this.parseTimeDurationFromType(newTimeGroup);
		});
		newTimeGroup.controls.type.valueChanges.subscribe(() => {
			this.parseTimeDurationFromType(newTimeGroup);
		});

		if (emitChangesOnAdd) this.group.controls.times.updateValueAndValidity();
	}
	addTime(type?: ComegoTimeType, duration?: number, reversi?: boolean) {
		const date = this.group.getRawValue()?.date || new Date();
		const timeValues = this.group.controls.times.getRawValue();
		const formEntriesWithEnd =
			timeValues?.map((t) => ({ endTs: coerceTimeFormat(t.end, date).getTime(), type: t.type })) ?? [];
		const lastFormByTime =
			formEntriesWithEnd.length > 0 ? formEntriesWithEnd.reduce((a, b) => (a.endTs >= b.endTs ? a : b)) : null;
		const wh = this.userSettingsQuery.getValue().workspace?.schedules?.workspace?.["workinghours"];

		const suggestion = this.comegoSuggestion.getSuggestedNextEntry({
			date,
			existingEntries: this.getRetainedExistingEntries(),
			formEntries: timeValues?.map((t) => ({ start: t.start, end: t.end, type: t.type })) ?? [],
			lastFormEntry: lastFormByTime ? { endTs: lastFormByTime.endTs, type: lastFormByTime.type } : undefined,
			schedule: this.activeSchedule,
			workingHours: wh ?? undefined,
			newWorkEntryDurationMs: this.newWorkEntryDuration ?? undefined,
			preferredType: type ?? undefined,
		});
		const nextType = suggestion.type;
		const prevTime = suggestion.startTime;
		const nextTime = suggestion.endTime;

		if (reversi)
			this.group.controls.times.insert(
				0,
				new FormGroup<TimesSingleControl>({
					name: new FormControl<string>(null),
					id: new FormControl(null),
					end: new FormControl(format(nextTime, "HH:mm")),
					type: new FormControl(nextType),
					start: new FormControl(format(prevTime, "HH:mm")),
					recording: new FormControl(false),
					user: new FormControl(null),
				}),
				{ emitEvent: false }
			);
		else
			this.group.controls.times.push(
				new FormGroup<TimesSingleControl>({
					name: new FormControl<string>(null),
					id: new FormControl(null),
					end: new FormControl(format(nextTime, "HH:mm")),
					type: new FormControl(nextType),
					start: new FormControl(format(prevTime, "HH:mm")),
					recording: new FormControl(false),
					user: new FormControl(null),
				}),
				{ emitEvent: false }
			);
		this.group.controls.times.updateValueAndValidity();
	}
	deleteItem(index: number) {
		this.group.controls.times.removeAt(index);
		this.group.controls.times.controls.forEach((x) => x.updateValueAndValidity());
		this.group.controls.times.updateValueAndValidity();
		this.group.updateValueAndValidity();
		if (this.isFullyRestrictedOnOwnTimes()) {
			this.applyLimitedUpdatePermissions();
		}
	}

	isItemDeletable(ctrlIndex: number): boolean {
		const ctrl = this.group.controls.times.controls[ctrlIndex];
		if (!ctrl || ctrl.disabled) return false;
		if (ctrl.controls.recording.value) return false;
		if (this.isEditingOwnTimes() && this.isPreExistingEntry(ctrl) && !this.canUpdateTimes$.value) return false;
		return true;
	}

	private checkValueOnceChange(value: any) {
		this.group.valueChanges
			.pipe(
				untilDestroyed(this),
				map((x) => stringify(x.times) === stringify(value)),
				tap((x) => log.debug("break rule check", x)),
				takeWhile((x) => x, true)
			)
			.subscribe((x) => !x && this.error$.next(null));
	}

	/** When the user cannot update times, only limited rows stay editable. */
	isEntryFormDisabled(ctrl: TimesControl): boolean {
		if (!this.isEditingOwnTimes()) return false;
		if (this.canUpdateTimes$.value) return false;
		if (this.isFullyRestrictedOnOwnTimes() && this.getLatestClosedTimeControl() === ctrl) return false;
		if (this.isCreateOnlyOnOwnTimes() && !this.isPreExistingEntry(ctrl)) return false;
		if (this.isPreExistingEntry(ctrl)) return true;
		return true;
	}

	private isEditingOwnTimes(): boolean {
		const formUserId = this.group.getRawValue()?.user?.id;
		const currentUserId = this.userSettingsQuery.getValue().id;
		if (!formUserId || formUserId === currentUserId) return true;
		const userSettings = this.userSettingsQuery.getValue();
		return !(isAdmin(userSettings) || isSupervisor(userSettings));
	}

	private isFullyRestrictedOnOwnTimes(): boolean {
		return this.isEditingOwnTimes() && !this.canUpdateTimes$.value && !this.canAddTime.value;
	}

	private isCreateOnlyOnOwnTimes(): boolean {
		return this.isEditingOwnTimes() && this.canAddTime.value && !this.canUpdateTimes$.value;
	}

	/** Chronologically last non-recording row with a start and end (by end time, tie-break by form order). */
	private getLatestClosedTimeControl(): TimesControl | null {
		const formDate = this.group.getRawValue()?.date;
		if (!formDate) return null;

		let latest: TimesControl | null = null;
		let latestEndTs = -1;
		let latestIndex = -1;

		this.group.controls.times.controls.forEach((ctrl, index) => {
			const raw = ctrl.getRawValue();
			if (raw.recording || !raw.start || !raw.end) return;
			const endTs = coerceTimeFormat(raw.end, formDate).getTime();
			if (!isValid(new Date(endTs))) return;
			if (endTs > latestEndTs || (endTs === latestEndTs && index > latestIndex)) {
				latestEndTs = endTs;
				latestIndex = index;
				latest = ctrl;
			}
		});

		return latest;
	}

	private buildEntryProvenanceMaps(): void {
		this.preExistingEntriesById = new Map((this.data.time?.value ?? []).filter((e) => e.id).map((e) => [e.id!, e]));
		for (const ctrl of this.group.controls.times.controls) {
			this.captureInitialBounds(ctrl);
		}
	}

	private captureInitialBounds(ctrl: TimesControl): void {
		const id = ctrl.controls.id.value;
		if (!id || this.initialBoundsById.has(id)) return;
		const raw = ctrl.getRawValue();
		if (!raw.start || !raw.end) return;
		this.initialBoundsById.set(id, {
			start: raw.start,
			end: raw.end,
			type: raw.type,
		});
	}

	private isPreExistingEntry(ctrl: TimesControl): boolean {
		const id = ctrl.controls.id.value;
		return !!id && this.preExistingEntriesById.has(id);
	}

	private getOtherFormEntries(
		excludeCtrl: TimesControl
	): Array<{ start: string; end: string; type: ComegoTimeType }> {
		return this.group.controls.times.controls
			.filter((c) => c !== excludeCtrl)
			.map((c) => c.getRawValue())
			.filter((t) => t.start && t.end)
			.map((t) => ({ start: t.start!, end: t.end!, type: t.type }));
	}

	/** Latest allowed end for a work row given schedule target and daily max across all other entries. */
	private getMaxAllowedWorkEnd(ctrl: TimesControl, formDate: Date): Date | null {
		const raw = ctrl.getRawValue();
		if (raw.type !== "work" || raw.recording || !raw.start) return null;

		const start = coerceTimeFormat(raw.start, formDate);
		if (!isValid(start)) return null;

		const otherFormEntries = this.getOtherFormEntries(ctrl);
		const existingEntries = this.getRetainedExistingEntries();
		const wh = this.userSettingsQuery.getValue().workspace?.schedules?.workspace?.workinghours;

		let maxEndTs = Infinity;

		const scheduleRemaining = this.comegoSuggestion.getRemainingTargetHoursForDay(
			this.activeSchedule,
			formDate,
			existingEntries,
			otherFormEntries
		);
		if (scheduleRemaining !== null) {
			maxEndTs = Math.min(maxEndTs, start.getTime() + scheduleRemaining);
		}

		const dailyRemaining = this.comegoSuggestion.getRemainingMaxHoursForDay(
			formDate,
			existingEntries,
			otherFormEntries,
			wh ?? undefined
		);
		if (dailyRemaining !== null) {
			maxEndTs = Math.min(maxEndTs, start.getTime() + dailyRemaining);
		}

		return maxEndTs === Infinity ? null : new Date(maxEndTs);
	}

	/** Clamps end to per-entry initial bound and combined schedule/daily-max limits. */
	private clampWorkEndToLimits(ctrl: TimesControl, formDate: Date, end: Date, initialEnd?: Date): Date {
		let clampedEnd = end;

		const isShortening = !!initialEnd && end.getTime() < initialEnd.getTime();
		if (!isShortening) {
			const maxAllowedEnd = this.getMaxAllowedWorkEnd(ctrl, formDate);
			if (maxAllowedEnd && clampedEnd.getTime() > maxAllowedEnd.getTime()) {
				this.showContactAdmin.update(true);
				clampedEnd = maxAllowedEnd;
			}
		}
		const start = coerceTimeFormat(ctrl.getRawValue().start!, formDate);
		if (clampedEnd.getTime() <= start.getTime()) {
			clampedEnd = addMinutes(start, 1);
		}
		return clampedEnd;
	}

	private clampEntryToInitialBounds(editedCtrl?: TimesControl, _field?: "start" | "end"): void {
		if (!this.isEditingOwnTimes()) return;
		if (this.isFullyRestrictedOnOwnTimes()) {
			this.clampLatestEndToInitialBounds(editedCtrl);
		}
	}

	private getInitialEndBound(ctrl: TimesControl): string | null {
		const id = ctrl.controls.id.value;
		if (id && this.initialBoundsById.has(id)) {
			return this.initialBoundsById.get(id)!.end;
		}
		if (id && this.preExistingEntriesById.has(id)) {
			const original = this.preExistingEntriesById.get(id)!;
			return original.end ? format(new Date(original.end), "HH:mm") : null;
		}
		return null;
	}

	/** When the user cannot edit or create times, latest end may only move earlier than at open. */
	private clampLatestEndToInitialBounds(editedCtrl?: TimesControl): void {
		if (!this.isFullyRestrictedOnOwnTimes()) return;

		const latest = this.getLatestClosedTimeControl();
		if (!latest) return;
		if (editedCtrl && editedCtrl !== latest) return;

		const formDate = this.group.getRawValue()?.date;
		if (!formDate) return;

		const raw = latest.getRawValue();
		if (!raw.start || !raw.end) return;

		const initialEndStr = this.getInitialEndBound(latest);
		if (!initialEndStr) return;

		const start = coerceTimeFormat(raw.start, formDate);
		const end = coerceTimeFormat(raw.end, formDate);
		const initialEnd = coerceTimeFormat(initialEndStr, formDate);
		if (!isValid(start) || !isValid(end) || !isValid(initialEnd)) return;

		const clampedEnd = this.clampWorkEndToLimits(latest, formDate, end, initialEnd);

		if (clampedEnd.getTime() !== end.getTime()) {
			latest.controls.end.patchValue(format(clampedEnd, "HH:mm"), { emitEvent: false });
			latest.updateValueAndValidity();
		}
	}

	private applyLimitedUpdatePermissions(): void {
		if (!this.canUpdateTimes$.value && this.isEditingOwnTimes()) {
			for (const ctrl of this.group.controls.times.controls) {
				if (this.isPreExistingEntry(ctrl)) {
					ctrl.disable({ emitEvent: false });
				}
			}

			if (this.isFullyRestrictedOnOwnTimes()) {
				const latest = this.getLatestClosedTimeControl();
				if (latest) {
					latest.enable({ emitEvent: false });
					latest.controls.start.enable({ emitEvent: false });
					latest.controls.type.disable({ emitEvent: false });
					latest.controls.name.disable({ emitEvent: false });
					latest.controls.end.enable({ emitEvent: false });
					this.captureInitialBounds(latest);
				}
			}

			if (this.isCreateOnlyOnOwnTimes()) {
				const latest = this.getLatestClosedTimeControl();
				if (latest) {
					latest.enable({ emitEvent: false });
					latest.controls.start.enable({ emitEvent: false });
					latest.controls.end.enable({ emitEvent: false });
				}
			}
		}
	}

	private enforceSubmitPermissions(): boolean {
		if (!this.isEditingOwnTimes()) return true;

		if (!this.canAddTime.value) {
			this.clampEntryToInitialBounds();
		}

		if (!this.canUpdateTimes$.value) {
			const formTimes = this.group.getRawValue().times ?? [];
			const formIds = new Set(formTimes.map((t) => t.id).filter(Boolean));

			for (const [id, original] of this.preExistingEntriesById) {
				if (!formIds.has(id)) {
					const start = new Date(original.start);
					const end = original.end ? new Date(original.end) : start;
					const type = (original.type ?? "work") as ComegoTimeType;
					this.addTimeRange(type, start, end, false, !original.end, original.name ?? null, id, "existing");
				}
			}

			for (const ctrl of this.group.controls.times.controls) {
				if (!this.isPreExistingEntry(ctrl)) continue;
				const id = ctrl.controls.id.value;
				const original = id ? this.preExistingEntriesById.get(id) : undefined;
				if (!original) continue;
				const origStart = format(new Date(original.start), "HH:mm");
				const origEnd = original.end ? format(new Date(original.end), "HH:mm") : null;
				const raw = ctrl.getRawValue();
				const type = (original.type ?? raw.type) as ComegoTimeType;
				const isLatestRestrictedEndEdit =
					this.isFullyRestrictedOnOwnTimes() && this.getLatestClosedTimeControl() === ctrl;

				if (isLatestRestrictedEndEdit) {
					if (raw.start !== origStart || raw.type !== type) {
						ctrl.patchValue(
							{
								start: origStart,
								type,
								name: original.name ?? raw.name,
							},
							{ emitEvent: false }
						);
					}
					this.clampLatestEndToInitialBounds(ctrl);
					continue;
				}

				if (raw.start !== origStart || raw.end !== origEnd || raw.type !== type) {
					ctrl.patchValue(
						{
							start: origStart,
							end: origEnd,
							type,
							name: original.name ?? raw.name,
						},
						{ emitEvent: false }
					);
				}
			}

			this.sortTimes(true);
			this.applyLimitedUpdatePermissions();
		}

		return true;
	}

	get pendingProjectionFix(): boolean {
		return !!this.data.pendingProjectionFix;
	}
	get violationNotices(): string[] {
		let translatedErrors: string[] = [];
		for (const notice of this.data.violationNotices ?? []) {
			const message =
				notice.message === "errors.times.comego.breakRuleNotice" ||
				notice.message === "errors.times.comego.breakRuleNotice-alert"
					? "errors.times.comego.breakRuleDetailed"
					: notice.message;
			translatedErrors.push(this.translate.instant(message, notice.args));
		}
		return translatedErrors;
	}
	/** Close without writing form data; caller persists the original violating projection. */
	saveWithViolation(): void {
		this.ref.close(PROCEED_WITH_VIOLATION);
	}
	async deleteTimes(ids: string[]) {
		if (!ids?.length) return;
		await this.comegoService.delete(...(ids.map((id) => ({ id })) as any));
	}
	private isRecreateDayFlow(): boolean {
		return !!(this.data.forceRecreateDay || this.data.pendingProjectionFix);
	}
	/** Persisted ids that recreate/projection submit will delete before re-adding. */
	private getIdsBeingReplaced(): Set<string> {
		if (this.isRecreateDayFlow()) {
			return new Set(this.getPersistedDayEntryIds());
		}
		const ids = new Set<string>();
		for (const id of this.data.idsToDelete ?? []) {
			if (id && !isDraftId(id)) ids.add(id);
		}
		return ids;
	}
	/**
	 * Store entries that should still count for overlap/duration/max checks.
	 * Excludes rows listed for delete on recreate and ids already present on form rows.
	 */
	private getRetainedExistingEntries(existingEntries?: ComegoTime[] | null): ComegoTime[] {
		const replaceIds = this.getIdsBeingReplaced();
		const formIds = new Set<string>();
		for (const ctrl of this.group.controls.times.controls) {
			const id = ctrl.value?.id;
			if (id && !isDraftId(id)) formIds.add(id);
		}
		return (existingEntries ?? this.existingEntries$.value ?? []).filter(
			(entry) => !!entry.id && !replaceIds.has(entry.id) && !formIds.has(entry.id)
		);
	}
	/** Loads same-day store entries when the dialog will recreate/replace the day on submit. */
	private loadExistingEntriesForRecreateFlow(): void {
		if (!this.isRecreateDayFlow()) return;
		const userId = this.group.getRawValue()?.user?.id;
		const date = this.group.getRawValue()?.date;
		if (!userId || !date || !isValid(date)) {
			this.existingEntries$.next([]);
			return;
		}
		const raw = this.comegoQuery.getAll({
			filterBy: (entry) => !!entry.start && entry.user?.id === userId && isSameDay(new Date(entry.start), date),
			sortBy: (a, b) => new Date(a.start).getTime() - new Date(b.start).getTime(),
		});
		this.existingEntries$.next([...raw].sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime()));
	}
	/** Collects persisted ids for the loaded day (excludes client-side drafts). */
	private getPersistedDayEntryIds(): string[] {
		const ids = new Set<string>();
		const addId = (id?: string | null) => {
			if (id && !isDraftId(id)) ids.add(id);
		};
		for (const entry of this.data.time?.value ?? []) {
			addId(entry.id);
		}
		for (const ctrl of this.group.controls.times.controls) {
			addId(ctrl.value?.id);
		}
		for (const id of this.data.idsToDelete ?? []) {
			addId(id);
		}
		for (const entry of this.existingEntries$.value ?? []) {
			addId(entry.id);
		}
		return [...ids];
	}
	async submit() {
		if (!this.enforceSubmitPermissions()) return;
		this.clampEntryToInitialBounds();
		let { times, user, date: now } = this.group.getRawValue();
		if (!times.length) return;
		const validTypes: ComegoTimeType[] = ["work", "pause", "absence"];
		times = times.map((x) => {
			if (!validTypes.includes(x.type)) {
				x.typeId = x.type;
				x.type = "absence";
			}
			return x;
		});
		this.isLoading.update(true);

		const idsToDelete = this.getPersistedDayEntryIds();
		let error: any;
		if (idsToDelete.length) {
			await this.deleteTimes(idsToDelete).catch((err) => {
				error = err;
				this.isLoading.update(false);
				pushError(err);
			});
		}
		if (error) return;
		const timesToAdd = times.map((r) => {
			const start = resolveEntryTime(r.start, now);
			const end = r.recording ? null : resolveEntryTime(r.end, now);
			return omit({ ...r, start, end, user }, "id") as any;
		});
		if (timesToAdd.length > 0) {
			await this.comegoService
				.add(...timesToAdd)
				.then(this.recordService.handleWorkingHourSuccess.bind(this.recordService))
				.catch(async (err: HttpErrorResponse) => {
					this.isLoading.update(false);
					if (isWorkingHoursError(err.error)) {
						this.error$.next(parseErrorResponse(err));
						this.checkValueOnceChange(times);
					} else {
						pushError(err);
					}
					return Promise.reject(err);
				})
				.then((created) => {
					this.app.notifySuccess("success.updated-plural");
					this.ref.close(created);
				});
		} else {
			this.app.notifySuccess("success.updated-plural");
			this.ref.close([]);
		}
		this.isLoading.update(false);
	}

	selectInput(ev: Event, timeInput: ElRefDirective) {
		const el: HTMLElement = timeInput.elementRef.nativeElement;
		if (!el) {
			return;
		}
		const input = el.querySelector("input");
		if (input) {
			ev.preventDefault();
			input.select();
		}
	}
	calcType(type: string) {
		switch (type) {
			case "work_history":
			case "pause":
			case "home_filled":
				return type;
			default:
				return "beach_access";
		}
	}

	parseTimeDurationFromType(formGroup: TimesControl) {
		const userSettings = this.userSettingsQuery.getValue();
		const absenceType = this.absenceSubcategories.find((absence) => absence.id === formGroup.controls.type.value);
		const userId = this.group.controls.user.value.id;
		if (!absenceType) {
			this.keepLatestEndDisabledIfRequired(formGroup);
			return;
		}
		formGroup.controls.end.enable();
		if (absenceType.isStatic || absenceType.durationType == "basedOnTargetHours") {
			if (!isAdmin(userSettings) && !isSubordinate(userSettings, userId)) formGroup.controls.end.disable();
		}
		const date = this.group.controls.date.value;
		let duration = "";
		if (absenceType.durationType == "basedOnTargetHours") {
			formGroup.controls.end.enable();
			duration = this.getScheduleForUser(userId, date);
		} else {
			duration = absenceType.duration;
		}
		const endString = this.sumTimes([formGroup.controls.start.value, duration]);
		formGroup.controls.end.patchValue(endString);
		this.sortTimes();
		this.keepLatestEndDisabledIfRequired(formGroup);
	}

	/** Re-apply latest-row end lock after absence duration parsing may have enabled it. */
	private keepLatestEndDisabledIfRequired(ctrl: TimesControl): void {
		if (!this.isEditingOwnTimes()) return;
		if (this.isFullyRestrictedOnOwnTimes()) return;
		if (!this.isCreateOnlyOnOwnTimes()) return;
		if (this.getLatestClosedTimeControl() !== ctrl) return;
		ctrl.controls.end.disable({ emitEvent: false });
	}

	private buildScheduleRangesText(userId: string, date: Date): string | null {
		const user = this.userSettingsQuery.getValue();
		const times = generateTimeTableFromSchedule({ ...user, id: userId }, date);
		const ranges = (times ?? [])
			.filter((time) => time.type === "work" && time.start && time.end)
			.map((time) => `${time.start} - ${time.end}`);
		if (!ranges.length) return null;
		return ranges.join(` ${this.translate.instant("utils.and")} `);
	}

	getScheduleForUser(userId: string, date: Date) {
		const user = this.userSettingsQuery.getValue();
		const times = generateTimeTableFromSchedule({ ...user, id: userId }, date);

		if (!times || times.length == 0) return "00:00";

		let hours: string[] = [];
		times
			.filter((time) => {
				return time.type == "work";
			})
			.forEach((time) => {
				hours.push(this.calculateScheduleDuration(time));
			});

		return this.sumTimes(hours);
	}
	sumTimes(times: string[]): string {
		let totalMinutes = 0;

		times.forEach((time) => {
			if (!time) return;
			const parts = time.split(":");
			if (parts.length < 2) return;
			const [hours, minutes] = parts.map(Number);
			if (Number.isNaN(hours) || Number.isNaN(minutes)) return;
			totalMinutes += hours * 60 + minutes;
		});

		const sumHours = Math.floor(totalMinutes / 60);
		const sumMinutes = totalMinutes % 60;

		return `${String(sumHours).padStart(2, "0")}:${String(sumMinutes).padStart(2, "0")}`;
	}
	calculateScheduleDuration(entry: ComeGoTime): string {
		if (!entry?.start || !entry?.end) return "00:00";
		const [startHour, startMinute] = entry.start.split(":").map(Number);
		const [endHour, endMinute] = entry.end.split(":").map(Number);
		if ([startHour, startMinute, endHour, endMinute].some((n) => Number.isNaN(n))) return "00:00";

		const startTotal = startHour * 60 + startMinute;
		const endTotal = endHour * 60 + endMinute;

		const diffMinutes = endTotal - startTotal;

		const hours = Math.floor(diffMinutes / 60);
		const minutes = diffMinutes % 60;

		return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
	}

	readonly absencesForUser = useAbsencesForUser();

	cancelAction() {
		this.ref.close(null);
	}
}
